import * as React from 'react';
import { AppBar, useStore, RefreshIconButton, useLogout, usePermissions, UserMenu } from 'react-admin';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import SettingsIcon from '@mui/icons-material/Settings';
import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { urlLogo } from '../../../utils/urlBase';
import { Button, Avatar, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ExitIcon from '@mui/icons-material/PowerSettingsNew';

/* SETUP COSTANTI GLOBALI - inizio */
import config from '../../../config/config.json';

const host = config.production.host;
const versione = config.infoapp.versione;
const nome = config.infoapp.nome;
/* SETUP COSTANTI GLOBALI - fine */

const SettingsButton = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button sx={{ color: 'white' }} component={Link} to="/settingspage">
            <SettingsIcon />
        </Button>
        <RefreshIconButton />
    </Box>
);

const AvatarCustom = () => (
    <Avatar
        sx={{
            height: 40,
            width: 40,
            bgcolor: "#41ad72",
            color: "#fff",
            fontWeight: 600,
            boxShadow: 2,
        }}
    >
        <PersonIcon />
    </Avatar>
);

const MyLogoutButton = forwardRef((props, ref) => {
    const logout = useLogout();

    const clearSpecificLocalStorageKeys = () => {
        // Lista delle chiavi specifiche da rimuovere dal localStorage
        const keysToRemove = [
            'dashboard-visible-columns',
            'players-view-visible-columns',
            'squadre-visible-columns',
            'partite-visible-columns',
            'classifica-visible-columns'
        ];

        // Rimuove ogni chiave specifica
        keysToRemove.forEach(key => {
            if (localStorage.getItem(key) !== null) {
                localStorage.removeItem(key);
                console.log(`Rimossa chiave: ${key}`);
            }
        });
    };

    const handleLogout = () => {
        try {
            // Pulisce le chiavi specifiche del localStorage PRIMA della pulizia generale
            clearSpecificLocalStorageKeys();

            // Logica per il logout esistente...
            if ('caches' in window) {
                caches.keys().then(function (cacheNames) {
                    cacheNames.forEach(function (cacheName) {
                        caches.delete(cacheName);
                    });
                });
            }
            localStorage.clear();
            sessionStorage.clear();
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.getRegistrations().then(function (registrations) {
                    registrations.forEach(function (registration) {
                        registration.unregister();
                    });
                });
            }
            const event = new Event('RA/RESET');
            document.dispatchEvent(event);
            logout();
            window.location.reload();
        } catch (error) {
            console.error('Errore durante la pulizia del localStorage:', error);
            // Esegue comunque il logout anche se la pulizia fallisce
            logout();
        }
    };

    return (
        <MenuItem onClick={handleLogout} ref={ref} {...props}>
            <ListItemIcon>
                <ExitIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Esci</ListItemText>
        </MenuItem>
    );
});

const useStyles = makeStyles({
    appBar: {
        background: 'linear-gradient(90deg, #41ad72 0%, #3474be 100%)',
        boxShadow: '0 4px 24px 0 rgba(60, 130, 40, 0.08)',
        '& .MuiButton-root, & .MuiIconButton-root': {
            color: '#fff'
        },
    },
    logoBox: {
        background: '#fff',
        borderRadius: '12px',
        padding: 8,
        marginRight: 20,
        display: 'flex',
        alignItems: 'center',
        boxShadow: '0 2px 8px rgba(65, 173, 114, 0.10)',
        height: 56,
    },
    logo: {
        maxHeight: 40,
        width: 'auto',
    },
    title: {
        flex: 1,
        fontWeight: 700,
        fontSize: '1.25rem',
        letterSpacing: 1,
        color: '#fff',
        textAlign: 'center',
        textShadow: '0 1px 2px rgba(60, 130, 40, 0.2)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    userInfo: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        marginLeft: 20,
        color: '#fff',
        fontSize: '1rem'
    }
});

export const MyAppBar = props => {
    const classes = useStyles();
    const [nomeUtente] = useStore('profile.email');
    const [ruoloUtente] = useStore('profile.roles');
    const { permissions } = usePermissions();

    return (
        <AppBar
            className={classes.appBar}
            {...props}
            userMenu={
                <UserMenu icon={<AvatarCustom />}>
                    <MenuItem disabled>
                        <ListItemIcon>
                            <PersonIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            <div>
                                <strong>{nomeUtente || 'Utente'}</strong>
                                <br />
                                <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                                    Ruolo: {ruoloUtente || '-'}
                                </span>
                            </div>
                        </ListItemText>
                    </MenuItem>
                    <MyLogoutButton />
                </UserMenu>
            }
            toolbar={
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    py: 1,
                    px: { xs: 1, sm: 3 },
                    ml: 2
                }}>
                    {/* Logo */}
                    <Box className={classes.logoBox}>
                        <img src={urlLogo} alt="Logo WebApp" className={classes.logo} />
                    </Box>

                    {/* Titolo centrale */}
                    <Typography className={classes.title}>
                        Gestione {nome} <span style={{ fontWeight: 300 }}>{versione}</span>
                    </Typography>

                    {/* Sezione info utente + avatar */}
                    <Box className={classes.userInfo}>
                        <span><strong>{nomeUtente || 'Utente'}</strong></span>
                        <Typography variant="body2" color="inherit" sx={{ fontSize: '0.95rem' }}>
                            Ruolo: {ruoloUtente || '-'}
                        </Typography>
                    </Box>

                    {/* Pulsanti admin solo per admin */}
                    {permissions === "admin" && (
                        <SettingsButton />
                    )}
                </Box>
            }
        />
    );
};